/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router';
import { ProtectedRoute as KitProtectedRoute, useAuth } from '@vaas/react-ui-kit/auth';
import { useTranslation } from 'react-i18next';

import { BootScreen } from '@/components/loading/BootScreen';
import { dismissBootSplash } from '@/components/loading/bootSplash';
import { loginRequest } from './msalConfig';
import { useForbidden, useSessionExpired } from './accessState';

/** Route guard backed by the shared kit auth (redirect in MSAL mode). */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { mode, status } = useAuth();
  const forbidden = useForbidden();
  const expired = useSessionExpired();

  // Outside MSAL the kit offers a sign-in button rather than redirecting, and nothing else on
  // that path clears the splash.
  const awaitingSignInPrompt = mode !== 'msal' && status === 'unauthenticated';
  useEffect(() => {
    if (awaitingSignInPrompt) dismissBootSplash();
  }, [awaitingSignInPrompt]);

  // Mirrors the kit's own gate condition, so we can cover its bare-text fallback rather than fork
  // the component and lose the sign-in redirect it fires while unauthenticated.
  const handshaking = status === 'loading' || (mode === 'msal' && status !== 'authenticated');

  // Keep forbidden users on /unauthorized instead of re-entering the protected shell loop.
  if (forbidden) {
    return <Navigate to="/unauthorized?reason=forbidden" replace />;
  }
  // A 401 leaves MSAL holding a stale token, so block navigation until re-auth clears this.
  if (expired) {
    return <Navigate to="/unauthorized?reason=expired" replace />;
  }
  return (
    <>
      {/* A cold load is still covered by the index.html splash; this is what a mid-session
          re-auth gets. */}
      {handshaking && <BootScreen status={t('common.signingIn')} />}
      <KitProtectedRoute loginRequest={loginRequest}>{children}</KitProtectedRoute>
    </>
  );
}
